import { ref, computed } from 'vue'
import { listSessions1 } from '@/gen/clients/research-chat-controller/listSessions1'
import { createSession1 } from '@/gen/clients/research-chat-controller/createSession1'
import { getSession1 } from '@/gen/clients/research-chat-controller/getSession1'
import { sendMessage1 as sendMessageApi } from '@/gen/clients/research-chat-controller/sendMessage1'
import { getMessage1 as getMessage } from '@/gen/clients/research-chat-controller/getMessage1'
import { getMessageStatus1 } from '@/gen/clients/research-chat-controller/getMessageStatus1'
import { deleteSession1 as deleteSessionApi } from '@/gen/clients/research-chat-controller/deleteSession1'
import { updateSession1 as updateSessionApi } from '@/gen/clients/research-chat-controller/updateSession1'
import type { OverviewResponse } from '@/gen/types/OverviewResponse'
import { DEFAULT_WEB_SEARCH_ENABLED } from '@/feature/runtimeDefaults'
import type { GenerationDebugStep } from '@/types/generationDebug'

interface Citation {
  title?: string
  url?: string
  snippet?: string
}

interface ResearchChatMessage {
  uuid?: string
  role?: string
  content?: string
  creationTimestamp?: string
  taskStatus?: string
  citations?: Citation[]
  errorCode?: number
  errorType?: string
  retryable?: boolean
  errorMessage?: string
  debugSteps?: GenerationDebugStep[]
}

interface SessionWithMessages {
  uuid?: string
  title?: string
  creationTimestamp?: string
  updateTimestamp?: string
  messages?: ResearchChatMessage[]
}

export interface ChatMessage {
  uuid: string
  role: 'user' | 'assistant'
  content: string
  isLoading?: boolean
  creationTimestamp?: string
  citations?: Citation[]
  errorCode?: number
  errorType?: string
  retryable?: boolean
  errorMessage?: string
  debugSteps?: GenerationDebugStep[]
}

export type { Citation }

const POLL_INTERVAL = 1000
const MAX_POLL_ATTEMPTS = 120
const FINAL_TASK_STATUSES = new Set(['FINISHED', 'COMPLETED', 'SUCCESS'])
const ERROR_TASK_STATUSES = new Set(['ERROR', 'FAILED', 'FAILURE', 'CANCELLED', 'CANCELED'])
const DEFAULT_RESEARCH_ERROR_MESSAGE = 'Failed to get response. Please try again.'

const RESEARCH_ERROR_TYPE_MESSAGES: Record<string, string> = {
  RATE_LIMIT_EXCEEDED: 'Rate limit reached. Please retry shortly.',
  AI_OVERLOADED: 'AI provider is overloaded. Please retry shortly.',
  FLEX_TIER_CAPACITY: 'AI provider capacity is currently limited. Please retry shortly.',
  AI_SERVICE_DOWN: 'AI provider is temporarily unavailable. Please retry shortly.',
  AI_SERVICE_ERROR: 'AI provider reported an internal error. Please retry shortly.',
  BAD_GATEWAY: 'Upstream provider gateway error. Please retry shortly.',
  GATEWAY_TIMEOUT: 'AI provider timed out. Please retry shortly.',
  REQUEST_TOO_LARGE: 'Request exceeds model context limits. Please shorten your prompt.',
  INVALID_MODEL_REQUEST: 'Request could not be processed by the selected model.',
  UNPROCESSABLE_ENTITY: 'Request contains unsupported or invalid content.',
  AUTHENTICATION_ERROR: 'Model provider authentication failed.',
  PERMISSION_ERROR: 'Model provider permission denied.'
}

let instance: ReturnType<typeof createResearchChat> | null = null

const normalizeTaskStatus = (taskStatus?: string): string => {
  return taskStatus?.trim().toUpperCase() ?? ''
}

const isFinalTaskStatus = (taskStatus?: string): boolean => {
  return FINAL_TASK_STATUSES.has(normalizeTaskStatus(taskStatus))
}

const isErrorTaskStatus = (taskStatus?: string): boolean => {
  return ERROR_TASK_STATUSES.has(normalizeTaskStatus(taskStatus))
}

const isPendingTaskStatus = (taskStatus?: string): boolean => {
  const normalized = normalizeTaskStatus(taskStatus)
  if (!normalized) return false
  if (isFinalTaskStatus(normalized)) return false
  if (isErrorTaskStatus(normalized)) return false
  return true
}

const resolveResearchErrorMessage = (message?: ResearchChatMessage): string => {
  const explicitMessage = message?.errorMessage?.trim()
  if (explicitMessage) {
    return explicitMessage
  }

  const fallbackByType = message?.errorType
    ? RESEARCH_ERROR_TYPE_MESSAGES[message.errorType]
    : undefined
  if (fallbackByType) {
    return fallbackByType
  }

  return DEFAULT_RESEARCH_ERROR_MESSAGE
}

const buildErrorChatMessage = (messageId: string, message?: ResearchChatMessage): ChatMessage => {
  const resolvedMessage = resolveResearchErrorMessage(message)
  const canRetry = Boolean(message?.retryable)

  return {
    uuid: message?.uuid || messageId,
    role: 'assistant',
    content: canRetry ? `${resolvedMessage} You can retry.` : resolvedMessage,
    isLoading: false,
    creationTimestamp: message?.creationTimestamp,
    citations: message?.citations,
    errorCode: message?.errorCode,
    errorType: message?.errorType,
    retryable: message?.retryable,
    errorMessage: message?.errorMessage,
    debugSteps: message?.debugSteps
  }
}

function createResearchChat() {
  const sessions = ref<OverviewResponse[]>([])
  const currentSession = ref<SessionWithMessages | null>(null)
  const messages = ref<ChatMessage[]>([])
  const webSearchEnabled = ref(DEFAULT_WEB_SEARCH_ENABLED)
  const isLoadingSessions = ref(false)
  const isLoadingMessages = ref(false)
  const isSending = ref(false)
  const isActive = ref(false)
  const activePolls = new Set<string>()

  const sortedSessions = computed(() => {
    return [...sessions.value].sort((a, b) => {
      const dateA = a.updateTimestamp || a.creationTimestamp || ''
      const dateB = b.updateTimestamp || b.creationTimestamp || ''
      return new Date(dateB).getTime() - new Date(dateA).getTime()
    })
  })

  const parseMessages = (rawMessages: ResearchChatMessage[]): ChatMessage[] => {
    return rawMessages.map(msg => {
      const isPending = isPendingTaskStatus(msg.taskStatus)
      const hasErrorStatus = isErrorTaskStatus(msg.taskStatus)
      const content = hasErrorStatus ? resolveResearchErrorMessage(msg) : msg.content || ''
      return {
        uuid: msg.uuid || crypto.randomUUID(),
        role: msg.role?.toLowerCase() === 'user' ? 'user' : 'assistant',
        content,
        isLoading: isPending,
        creationTimestamp: msg.creationTimestamp,
        citations: msg.citations,
        errorCode: msg.errorCode,
        errorType: msg.errorType,
        retryable: msg.retryable,
        errorMessage: msg.errorMessage,
        debugSteps: msg.debugSteps
      }
    })
  }

  async function pollMessageStatus(
    messageId: string,
    sessionId: string
  ): Promise<'finished' | 'error' | 'aborted' | 'timeout'> {
    let attempts = 0

    while (attempts < MAX_POLL_ATTEMPTS) {
      if (!isActive.value || currentSession.value?.uuid !== sessionId) {
        return 'aborted'
      }

      try {
        const status = await getMessageStatus1(messageId)

        if (isFinalTaskStatus(status.taskStatus)) {
          return 'finished'
        }

        if (isErrorTaskStatus(status.taskStatus)) {
          return 'error'
        }

        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        attempts++
      } catch (error) {
        console.error('Failed to poll message status:', error)
        return 'error'
      }
    }

    return 'timeout'
  }

  async function pollAndUpdateMessage(messageId: string, sessionId: string) {
    const pollKey = `${sessionId}-${messageId}`
    if (activePolls.has(pollKey)) return
    activePolls.add(pollKey)

    const pollResult = await pollMessageStatus(messageId, sessionId)

    activePolls.delete(pollKey)

    if (!isActive.value || currentSession.value?.uuid !== sessionId) return

    const messageIndex = messages.value.findIndex(m => m.uuid === messageId)
    if (messageIndex === -1) return

    if (pollResult === 'finished') {
      const fullMessage = (await getMessage(messageId)) as ResearchChatMessage

      if (currentSession.value?.uuid !== sessionId) return

      messages.value[messageIndex] = {
        uuid: fullMessage.uuid || messageId,
        role: 'assistant',
        content: fullMessage.content || '',
        isLoading: false,
        creationTimestamp: fullMessage.creationTimestamp,
        citations: fullMessage.citations,
        errorCode: fullMessage.errorCode,
        errorType: fullMessage.errorType,
        retryable: fullMessage.retryable,
        errorMessage: fullMessage.errorMessage,
        debugSteps: fullMessage.debugSteps
      }
    } else {
      let failedMessage: ResearchChatMessage | undefined
      try {
        failedMessage = (await getMessage(messageId)) as ResearchChatMessage
      } catch (error) {
        console.error('Failed to fetch research error details:', error)
      }

      messages.value[messageIndex] = buildErrorChatMessage(messageId, failedMessage)
    }
  }

  const setActive = (active: boolean) => {
    isActive.value = active
    if (!active) {
      activePolls.clear()
      return
    }

    if (currentSession.value?.uuid) {
      const pendingMessages = messages.value.filter(m => m.isLoading && m.role === 'assistant')
      pendingMessages.forEach(msg => {
        pollAndUpdateMessage(msg.uuid, currentSession.value!.uuid!)
      })
    }
  }

  const loadSessions = async () => {
    isLoadingSessions.value = true
    try {
      const response = await listSessions1()
      sessions.value = response || []
    } catch (error) {
      console.error('Failed to load sessions:', error)
      sessions.value = []
    } finally {
      isLoadingSessions.value = false
    }
  }

  const selectSession = async (sessionId: string) => {
    isLoadingMessages.value = true
    try {
      const response = (await getSession1(sessionId)) as SessionWithMessages
      currentSession.value = response
      const rawMessages = response.messages || []
      messages.value = parseMessages(rawMessages)

      const pendingMessages = messages.value.filter(m => m.isLoading && m.role === 'assistant')
      pendingMessages.forEach(msg => {
        pollAndUpdateMessage(msg.uuid, sessionId)
      })
    } catch (error) {
      console.error('Failed to load session:', error)
    } finally {
      isLoadingMessages.value = false
    }
  }

  const createNewSession = async () => {
    try {
      const response = await createSession1({})
      if (response.uuid) {
        await loadSessions()
        await selectSession(response.uuid)
      }
    } catch (error) {
      console.error('Failed to create session:', error)
    }
  }

  const refreshSessionTitle = async () => {
    if (!currentSession.value?.uuid) return

    try {
      const response = (await getSession1(currentSession.value.uuid)) as SessionWithMessages
      const newTitle = response.title

      if (newTitle && newTitle !== currentSession.value.title) {
        currentSession.value.title = newTitle

        const sessionIndex = sessions.value.findIndex(s => s.uuid === currentSession.value?.uuid)
        if (sessionIndex !== -1) {
          sessions.value[sessionIndex].title = newTitle
        }
      }
    } catch (error) {
      console.error('Failed to refresh session title:', error)
    }
  }

  const updateSessionTitle = async (sessionId: string, title: string) => {
    try {
      await updateSessionApi(sessionId, { title })

      if (currentSession.value?.uuid === sessionId) {
        currentSession.value.title = title
      }

      const sessionIndex = sessions.value.findIndex(s => s.uuid === sessionId)
      if (sessionIndex !== -1) {
        sessions.value[sessionIndex].title = title
      }
    } catch (error) {
      console.error('Failed to update session title:', error)
    }
  }

  const sendMessage = async (
    content: string,
    messageWebSearchEnabled: boolean = webSearchEnabled.value
  ) => {
    if (!currentSession.value?.uuid) {
      await createNewSession()
      if (!currentSession.value?.uuid) return
    }

    const sessionId = currentSession.value.uuid

    const userMessage: ChatMessage = {
      uuid: crypto.randomUUID(),
      role: 'user',
      content,
      creationTimestamp: new Date().toISOString()
    }
    messages.value.push(userMessage)

    const assistantPlaceholder: ChatMessage = {
      uuid: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      isLoading: true,
      creationTimestamp: new Date().toISOString()
    }
    messages.value.push(assistantPlaceholder)
    let assistantMessageId = assistantPlaceholder.uuid

    isSending.value = true

    try {
      const response = await sendMessageApi(sessionId, {
        content,
        webSearchEnabled: messageWebSearchEnabled
      })

      if (response.uuid) {
        assistantMessageId = response.uuid

        const mappedPlaceholderIndex = messages.value.findIndex(
          m => m.uuid === assistantPlaceholder.uuid
        )
        if (mappedPlaceholderIndex !== -1) {
          messages.value[mappedPlaceholderIndex] = {
            ...messages.value[mappedPlaceholderIndex],
            uuid: assistantMessageId
          }
        }

        const pollResult = await pollMessageStatus(assistantMessageId, sessionId)

        if (!isActive.value || currentSession.value?.uuid !== sessionId) return

        if (pollResult === 'finished') {
          const fullMessage = (await getMessage(assistantMessageId)) as ResearchChatMessage

          if (!isActive.value || currentSession.value?.uuid !== sessionId) return

          const placeholderIndex = messages.value.findIndex(
            m => m.uuid === assistantMessageId || m.uuid === assistantPlaceholder.uuid
          )

          if (placeholderIndex !== -1) {
            messages.value[placeholderIndex] = {
              uuid: fullMessage.uuid || response.uuid,
              role: 'assistant',
              content: fullMessage.content || '',
              isLoading: false,
              creationTimestamp: fullMessage.creationTimestamp,
              citations: fullMessage.citations,
              errorCode: fullMessage.errorCode,
              errorType: fullMessage.errorType,
              retryable: fullMessage.retryable,
              errorMessage: fullMessage.errorMessage,
              debugSteps: fullMessage.debugSteps
            }
          }

          await refreshSessionTitle()
        } else {
          if (currentSession.value?.uuid !== sessionId) return

          let failedMessage: ResearchChatMessage | undefined
          try {
            failedMessage = (await getMessage(assistantMessageId)) as ResearchChatMessage
          } catch (error) {
            console.error('Failed to fetch research error details:', error)
          }

          const placeholderIndex = messages.value.findIndex(
            m => m.uuid === assistantMessageId || m.uuid === assistantPlaceholder.uuid
          )
          if (placeholderIndex !== -1) {
            messages.value[placeholderIndex] = buildErrorChatMessage(
              assistantMessageId,
              failedMessage
            )
          }
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      const placeholderIndex = messages.value.findIndex(
        m => m.uuid === assistantMessageId || m.uuid === assistantPlaceholder.uuid
      )
      if (placeholderIndex !== -1) {
        messages.value[placeholderIndex] = {
          ...messages.value[placeholderIndex],
          content: DEFAULT_RESEARCH_ERROR_MESSAGE,
          isLoading: false
        }
      }
    } finally {
      isSending.value = false
    }
  }

  const deleteSession = async (sessionId: string) => {
    try {
      await deleteSessionApi(sessionId)
      sessions.value = sessions.value.filter(s => s.uuid !== sessionId)

      if (currentSession.value?.uuid === sessionId) {
        currentSession.value = null
        messages.value = []
      }
    } catch (error) {
      console.error('Failed to delete session:', error)
    }
  }

  loadSessions()

  return {
    sessions: sortedSessions,
    currentSession,
    messages,
    webSearchEnabled,
    isLoadingSessions,
    isLoadingMessages,
    isSending,
    loadSessions,
    selectSession,
    createNewSession,
    sendMessage,
    deleteSession,
    updateSessionTitle,
    setActive
  }
}

export function useResearchChat() {
  if (!instance) {
    instance = createResearchChat()
  }
  return instance
}

export function resetResearchChat() {
  instance = null
}
